import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, CalendarDays } from "lucide-react";
import { fmtSession } from "@/lib/dates";
import { api } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

export const useCourses = () => {
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    api.get("/courses").then((r) => setCourses(Array.isArray(r.data) ? r.data : [])).catch(() => setCourses([]));
  }, []);
  return courses;
};

export const CourseRow = ({ c, i }) => (
  <Reveal delay={i * 0.06}>
    <Link
      to={`/programmes/${c.slug}`}
      data-testid={`course-row-${c.slug}`}
      className="group grid grid-cols-12 items-center gap-4 border-t border-current/15 py-8 transition-colors duration-500 md:py-10"
    >
      <span className="col-span-2 font-mono text-xs opacity-70 md:col-span-1">0{i + 1}</span>
      <div className="col-span-10 md:col-span-6">
        <h3 className="font-display text-3xl font-medium leading-none transition-transform duration-500 group-hover:translate-x-3 sm:text-5xl">{c.title}</h3>
        <p className="mt-3 max-w-lg text-sm opacity-80">{c.summary}</p>
        {c.provider && <p data-testid={`rto-badge-${c.slug}`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ochre px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-sand"><BadgeCheck className="h-3.5 w-3.5" /> RTO accredited</p>}
        {c.next_session && (
          <p data-testid={`next-session-${c.slug}`} className="mt-3 inline-flex items-center gap-2 rounded-full border border-current/25 px-3 py-1 font-mono text-[11px] uppercase tracking-wider">
            <CalendarDays className="h-3.5 w-3.5 text-ochre" /> Next: {fmtSession(c.next_session)}
          </p>
        )}
      </div>
      <div className="col-span-12 h-48 overflow-hidden rounded-2xl md:col-span-4 md:h-32 md:[clip-path:inset(0_100%_0_0_round_16px)] md:group-hover:[clip-path:inset(0_0%_0_0_round_16px)] md:transition-[clip-path] md:duration-700">
        <img src={c.image} alt={c.title} className="h-full w-full object-cover" />
      </div>
      <span className="col-span-12 flex items-center justify-between md:col-span-1 md:justify-end">
        <span className="eyebrow text-ochre md:hidden">{c.category}</span>
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-current/30 transition-all duration-500 group-hover:rotate-45 group-hover:border-ochre group-hover:bg-ochre group-hover:text-sand">
          <ArrowUpRight className="h-5 w-5" />
        </span>
      </span>
    </Link>
  </Reveal>
);

export const ProgrammesPreview = () => {
  const courses = useCourses();
  if (!courses.length) return null;
  return (
    <section data-testid="programmes-preview" className="bg-ink px-5 py-24 text-sand sm:px-10 lg:py-36">
      <div className="mx-auto max-w-[1440px]">
        <Reveal className="mb-12">
          <p className="eyebrow text-amber">Now enrolling</p>
          <h2 className="mt-4 font-display text-4xl font-medium tracking-tight sm:text-6xl">Training programmes</h2>
        </Reveal>
        {courses.map((c, i) => <CourseRow key={c.slug} c={c} i={i} />)}
      </div>
    </section>
  );
};
